import * as React from 'react';

import { useQuery } from '@apollo/client';

import { get } from 'js/lib/user';

import { PROGRAM_VISIBILITY_RULE } from 'bundles/enterprise-legacy-learner-home/constants/ProgramCommonConstants';
import FindUserEmailsByUserIdQuery from 'bundles/enterprise-legacy-learner-home/queries/FindUserEmailsByUserIdQuery';
import type {
  FindUserEmailsByUserIdQueryResult,
  FindUserEmailsByUserIdQueryVariables,
} from 'bundles/enterprise-legacy-learner-home/queries/FindUserEmailsByUserIdQuery';
import type { EnterpriseNoticeAcceptanceLogsV1 } from 'bundles/naptimejs/resources/__generated__/EnterpriseNoticeAcceptanceLogsV1';
import EnterpriseNoticeAcceptanceQuery from 'bundles/program-home/queries/EnterpriseNoticeAcceptanceQuery';
import type {
  EnterpriseNoticeAcceptanceQuery as EnterpriseNoticeAcceptanceQueryData,
  EnterpriseNoticeAcceptanceQueryVariables,
} from 'bundles/program-home/queries/EnterpriseNoticeAcceptanceQuery';
import { useUserExperienceMemberships } from 'bundles/unified-common/contexts/UserExperienceMembershipsContext';
import EnterpriseOrgUserExperienceContext from 'bundles/unified-common/contexts/org-scoped/EnterpriseOrgUserExperienceContext';
import { usePreviewProgramIds } from 'bundles/unified-common/contexts/org-scoped/OrgScopedImpersonationContext';
import { useThirdPartyOrganization } from 'bundles/unified-common/contexts/org-scoped/ThirdPartyOrganizationContext';
import { useRecoveryEmail } from 'bundles/unified-common/hooks/useRecoveryEmail';

/**
 * Provides org-scoped user experience data: program memberships for the current org,
 * acceptance notice logs, and pre-join check. Must be rendered inside both
 * UnifiedUserExperienceHandler (to read memberships) and a provider that supplies
 * ThirdPartyOrganizationContext (e.g. EnterpriseOrgHomePageProvider).
 */
const EnterpriseOrgUserExperienceDataProvider = ({ children }: React.PropsWithChildren<{}>) => {
  const userId = get()?.id;
  const thirdPartyOrganization = useThirdPartyOrganization();
  const previewProgramIds = usePreviewProgramIds();
  const {
    programMemberships,
    isLoading: isProgramMembershipsAndInvitationsLoading,
    isError: isMembershipsLoadError,
  } = useUserExperienceMemberships();

  const requirePreJoinCheck =
    thirdPartyOrganization?.programVisibilityRule === PROGRAM_VISIBILITY_RULE.ACCEPTED_PRIVACY_NOTICE;

  const programMembershipsAndInvitations = React.useMemo(() => {
    if (!programMemberships?.length || !thirdPartyOrganization?.id) {
      return undefined;
    }
    let memberships = programMemberships.filter(
      (edge) => edge?.node?.program?.naptimeThirdPartyOrganization?.id === thirdPartyOrganization.id
    );
    if (previewProgramIds.length > 0) {
      const previewSet = new Set(previewProgramIds);
      memberships = memberships.filter((edge) => previewSet.has(edge?.node?.program?.id ?? ''));
    }
    return memberships;
  }, [programMemberships, thirdPartyOrganization?.id, previewProgramIds]);

  const {
    data: acceptanceNoticeData,
    loading: isAcceptanceNoticeLogsLoading,
    refetch: refetchAcceptanceNoticeLogsFromQuery,
  } = useQuery<EnterpriseNoticeAcceptanceQueryData, EnterpriseNoticeAcceptanceQueryVariables>(
    EnterpriseNoticeAcceptanceQuery,
    {
      variables: {
        userId: userId?.toString() ?? '',
        thirdPartyOrganizationId: thirdPartyOrganization?.id ?? '',
        requirePreJoinCheck: requirePreJoinCheck ?? false,
      },
      skip: !userId || !thirdPartyOrganization?.id || !requirePreJoinCheck,
    }
  );

  const refetchAcceptanceNoticeLogs = React.useMemo(
    () => refetchAcceptanceNoticeLogsFromQuery ?? (() => Promise.resolve()),
    [refetchAcceptanceNoticeLogsFromQuery]
  );

  const acceptanceNoticeLogs = acceptanceNoticeData?.EnterpriseNoticeAcceptanceLogsV1?.getByUserIdAndOrganizationId
    ?.elements as EnterpriseNoticeAcceptanceLogsV1[] | undefined;

  // Fetch user emails for recovery email modal
  const {
    data: userEmailsData,
    loading: isUserEmailsLoading,
    refetch: refetchUserEmails,
  } = useQuery<FindUserEmailsByUserIdQueryResult, FindUserEmailsByUserIdQueryVariables>(FindUserEmailsByUserIdQuery, {
    variables: { userId: `${userId}` },
    skip: !userId,
    errorPolicy: 'all',
    ssr: false,
  });

  const userEmails = userEmailsData?.FindUserEmailsByUserIdResponse?.userEmails;
  // Fetch recovery email
  const { recoveryEmail, isRecoveryEmailLoading } = useRecoveryEmail(userId, thirdPartyOrganization?.id);

  const orgContextValue = React.useMemo(
    () => ({
      programMembershipsAndInvitations,
      isProgramMembershipsAndInvitationsLoading,
      isMembershipsLoadError: isMembershipsLoadError ?? false,
      acceptanceNoticeLogs,
      isAcceptanceNoticeLogsLoading,
      refetchAcceptanceNoticeLogs,
      requirePreJoinCheck,
      userEmails,
      isUserEmailsLoading,
      refetchUserEmails,
      recoveryEmail,
      isRecoveryEmailLoading,
    }),
    [
      programMembershipsAndInvitations,
      isProgramMembershipsAndInvitationsLoading,
      isMembershipsLoadError,
      acceptanceNoticeLogs,
      isAcceptanceNoticeLogsLoading,
      refetchAcceptanceNoticeLogs,
      requirePreJoinCheck,
      userEmails,
      isUserEmailsLoading,
      refetchUserEmails,
      recoveryEmail,
      isRecoveryEmailLoading,
    ]
  );

  return (
    <EnterpriseOrgUserExperienceContext.Provider value={orgContextValue}>
      {children}
    </EnterpriseOrgUserExperienceContext.Provider>
  );
};

export default EnterpriseOrgUserExperienceDataProvider;
